<template>
	<div class="costos_tab report_theme_container" data-theme="costos">
		<!-- ================================================================ -->
		<!-- Header Section                                                    -->
		<!-- ================================================================ -->
		<div class="header_section_header mb-5">
			<div class="header_left">
				<div class="header_title">Costos</div>
				<div class="header_description mb-4">
					Analiza la tendencia y composición de tus costos
				</div>

				<!-- Sub-Navigation Filters -->
				<div class="sub_nav_filters">
					<button v-for="period in filterOptions" :key="period.id" class="filter_btn"
						:class="{ 'is-active': filterPeriod === period.id }" @click="updateFilterPeriod(period.id)">
						{{ period.label }}
					</button>
				</div>
			</div>

			<!-- Center: filtros de visualización (Cuentas + tipos de contraparte) -->
			<div class="center_sub_nav">
				<div class="filters_row is-flex is-align-items-center">
					<div class="sub_nav_filters">
						<button type="button"
							class="filter_btn"
							:class="{ 'is-active': isCuentasMode }"
							@click.stop="setCuentasMode">
							Cuentas
						</button>
					</div>

					<MultiSelectDropdown
						:value="selectedCounterpartTypes"
						:options="counterpartTypeOptions"
						placeholder="Tipos de contraparte"
						@input="SET_SELECTED_COUNTERPART_TYPES" />
				</div>
			</div>

			<div class="right_actions is-flex">
				<div class="calendar_picker is-flex is-align-items-center mr-3">
					<AnualCalendar class="calendar" :value="endMonth" @onDateChange="updateEndMonth"
						:disableNewerDates="true" :disabledFrom="getNow" formatLabelDate="MMMM YYYY"
						:alignRight="true" />
				</div>
				<button class="theme_btn_white is-flex is-align-items-center">
					<DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
					Exportar
				</button>
			</div>
		</div>

		<!-- ================================================================ -->
		<!-- Summary Cards Section                                             -->
		<!-- Reusa CardVentas. El slot #extra muestra el badge "Margen Bruto", -->
		<!-- la conclusión inline que distingue Costos del resto de tabs.     -->
		<!-- ================================================================ -->
		<div class="cards_container is-flex mt-5">
			<CardVentas v-for="(card, index) in displayCards" :key="index" v-bind="card" class="mr-4">
				<template #extra>
					<div v-if="card.grossMargin" class="margin_extra is-flex is-align-items-center">
						<ChartLine class="margin_icon mr-1" />
						<span class="margin_label">Margen Bruto</span>
						<span class="margin_badge ml-auto">{{ card.grossMargin }}</span>
					</div>
				</template>
			</CardVentas>
		</div>

		<!-- Main Table Section -->
		<ExpensesEvolutionTable class="mt-6"
			title="Evolución de Costos"
			emptyMessage="No hay datos de costos para el período seleccionado."
			:monthHeaders="monthHeaders"
			:visibleRows="visibleRows"
			:totalRow="totalRow"
			:ratioRow="ratioRow"
			:level2RatioRows="level2RatioRows"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			@toggle-l2="toggleL2"
			@change-period="setPeriodRange"
			@retry="fetchCostsData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import AnualCalendar from "@/components/classification/AnualCalendar";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import ChartLine from "@/components/svg/ChartLine.vue";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import ExpensesEvolutionTable from "@/components/financialReports/ExpensesEvolutionTable.vue";
import {
	PERIOD_TYPES,
	COST_COUNTERPART_TYPES,
	COST_CARD_PROGRESS_COLORS,
	FILTER_PERIOD_OPTIONS,
} from "../config/reportStructure";
import { formatPercent, formatRangeLabel } from "../utils/periodFormatting";

const EMPTY_CARD = () => ({
	period: "-",
	amount: 0,
	mom: "",
	yoy: "",
	// "" → el v-if del slot oculta el badge mientras no haya datos. Mostrar
	// "—" daría la falsa señal de un dato calculado en cero.
	grossMargin: "",
	progressItems: [],
});

export default {
	name: "CostosTab",

	components: {
		AnualCalendar,
		DownloadFile,
		ChartLine,
		MultiSelectDropdown,
		CardVentas,
		ExpensesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			counterpartTypeOptions: COST_COUNTERPART_TYPES,
			expandedL2Rows: [],
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("costsReport", [
			"selectedCounterpartTypes",
			"monthHeaders",
			"isLoading",
			"error",
			"summaryCards",
		]),
		...mapGetters("costsReport", ["tableRows", "totalRow", "ratioRow", "level2RatioRows", "isCuentasMode"]),

		getNow() {
			return moment().toDate();
		},

		visibleRows() {
			return this.tableRows.filter(row => {
				if (row.level === 2) return true;
				return this.expandedL2Rows.includes(row.parent);
			});
		},

		displayCards() {
			if (!this.summaryCards) {
				return [EMPTY_CARD(), EMPTY_CARD(), EMPTY_CARD()];
			}
			const { current_month, ytd, ltm } = this.summaryCards;
			return [
				this.buildCard(current_month, { showMom: true }),
				this.buildCard(ytd),
				this.buildCard(ltm),
			];
		},

		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.selectedCounterpartTypes.join(","),
				this.filterPeriod,
			].join("|");
		},

		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("costsReport", ["fetchCostsData", "fetchCostsSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("costsReport", [
			"SET_SELECTED_COUNTERPART_TYPES",
		]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		setCuentasMode() {
			this.SET_SELECTED_COUNTERPART_TYPES([]);
		},
		toggleL2(label) {
			const index = this.expandedL2Rows.indexOf(label);
			if (index > -1) {
				this.expandedL2Rows.splice(index, 1);
			} else {
				this.expandedL2Rows.push(label);
			}
		},

		buildCard(card, { showMom = false } = {}) {
			return {
				period: formatRangeLabel(card.period_start, card.period_end),
				amount: card.amount,
				mom: showMom ? formatPercent(card.mom) : "",
				yoy: formatPercent(card.yoy),
				grossMargin: card.gross_margin != null ? formatPercent(card.gross_margin) : "",
				progressItems: (card.breakdown || []).map((item, i) => ({
					label: item.label,
					percent: item.percent,
					color: COST_CARD_PROGRESS_COLORS[i % COST_CARD_PROGRESS_COLORS.length],
				})),
			};
		},
	},

	mounted() {
		this.fetchCostsData();
		this.fetchCostsSummary();
	},

	watch: {
		fetchSignature() { this.fetchCostsData(); },
		summarySignature() { this.fetchCostsSummary(); },
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.costos_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

.header_section_header {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 16px 24px;

	position: sticky;
	top: var(--tab-sticky-top, 193px);
	z-index: 4;
	background: var(--report-bg);
	border-bottom: 1px solid var(--report-border);
	padding-bottom: 8px;
}

.center_sub_nav,
.right_actions {
	margin-left: auto;
}

@media (max-width: 600px) {
	.header_section_header {
		flex-direction: column;
		align-items: center;
	}

	.center_sub_nav,
	.right_actions {
		margin-left: 0;
	}

	.header_left {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}
}

.header_title {
	font-size: 32px;
	font-weight: 700;
	margin-bottom: 4px;
}

.header_description {
	font-size: 16px;
	color: var(--report-text-muted);
}

.filters_row {
	gap: 12px;
}

.sub_nav_filters {
	display: inline-flex;
	background: #ffffff;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: #4b5563;
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: #f9fafb; }

	&.is-active {
		background: var(--report-primary);
		color: #ffffff;
	}
}

.calendar_picker {
	background: var(--report-primary);
	color: white;
	border-radius: 8px;
	font-weight: 500;

	::v-deep .calendar-label {
		color: white !important;
	}
}

.theme_btn_white {
	background: white;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 8px 16px;
	font-weight: 500;
	color: #374151;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	cursor: pointer;

	&:hover { background: #f9fafb; }
}

.cards_container {
	width: 100%;
}

// "Margen Bruto X%" — signature de Costos. Icon+label a la izquierda sobre la
// card (sin fondo), y el porcentaje en una pill aparte empujada a la derecha
// con ml-auto. Mismo patrón visual que Eficiencia en Gastos.
.margin_extra {
	width: 100%;
	font-size: 13px;
}

.margin_icon {
	width: 14px;
	height: 14px;
	color: #9ca3af;
}

.margin_label {
	color: var(--report-text-muted);
	font-weight: 500;
}

.margin_badge {
	display: inline-flex;
	align-items: center;
	padding: 4px 10px;
	border-radius: 999px;
	background: var(--report-accent-bg);
	color: var(--report-accent-text);
	border: 1px solid var(--report-accent-border);
	font-size: 12px;
	font-weight: 700;
}
</style>
